import React, { ReactNode, useEffect } from 'react';

import { Pressable, Skeleton, Text } from '@rbilabs/universal-components';
import { Animated } from 'react-native';

import { useGeolocation } from 'state/geolocation';
import { useReversedUIContext } from 'state/reversed-ui';
import { theme } from 'styles/configure-theme';

export const StyledCtaButton = Pressable.withConfig({
  display: 'flex',
  flexDirection: 'row',
  _web: {
    cursor: 'pointer',
  },
  fontSize: {
    base: 'sm',
    md: 'md',
  },
});

interface CtaButtonProps {
  text?: string;
  cta: () => void;
  icon?: ReactNode;
  enableAnimation?: boolean;
}

const CtaButton = ({ text, cta, icon, enableAnimation = false }: CtaButtonProps) => {
  const { reversedUI } = useReversedUIContext();
  const { isPermissionGranted } = useGeolocation();

  const scaleAnim = React.useRef(new Animated.Value(1.5));
  const paddingRightAnim = React.useRef(new Animated.Value(20));
  const paddingTopAnim = React.useRef(new Animated.Value(0));

  useEffect(() => {
    if (text && enableAnimation && isPermissionGranted) {
      Animated.timing(scaleAnim.current, {
        toValue: 1,
        duration: 500,
        useNativeDriver: false,
      }).start();
      Animated.timing(paddingRightAnim.current, {
        toValue: 0,
        duration: 500,
        useNativeDriver: false,
      }).start();
      Animated.timing(paddingTopAnim.current, {
        toValue: 3,
        duration: 500,
        useNativeDriver: false,
      }).start();
    }
  }, [text, isPermissionGranted]);

  const animationViewStyles =
    isPermissionGranted && enableAnimation && !!text
      ? [
          {
            transform: [{ scale: scaleAnim.current }],
            paddingRight: paddingRightAnim.current,
            paddingTop: paddingTopAnim.current,
          },
        ]
      : [];

  const component =
    text !== '' || !isPermissionGranted ? (
      <Text
        color={reversedUI ? theme.token('text-reversed') : theme.token('text-default')}
        fontWeight={isPermissionGranted && enableAnimation ? 'bold' : undefined}
        pt={!enableAnimation ? '3px' : undefined}
      >
        {text}
      </Text>
    ) : (
      <Skeleton mt={1} rounded="full" h="$5" width={'90px'} />
    );

  return (
    <StyledCtaButton onPress={cta} accessibilityRole="button">
      <Animated.View style={animationViewStyles}>{component}</Animated.View>
      {icon}
    </StyledCtaButton>
  );
};

export default CtaButton;
